/** Real Insights entry point for asking, reopening and correcting merchant knowledge, inside one card. */
import { useState } from 'react'
import {
  STORE_KNOWLEDGE_CAPABILITY,
  type KnowledgeCatalogItem,
  type KnowledgeEntry,
  type StoreKnowledgeWrite,
} from '@testfeed/integration-contracts'
import { storeKnowledgeCopy as copy } from '@testfeed/product-language'
import type { ShopifyTokenSource } from '../../shopifyApiFetch'
import { resolveMerchantErrorMessage } from '../../merchantErrors'
import { useStoreKnowledge, useSaveStoreKnowledge } from '../../hooks/useStoreKnowledge'
import { useShopifyAppContext, buildShopifyAppScopeKey } from '../../ShopifyAppContext'
import { KnowledgeItemPicker, knowledgeItemLabel } from './KnowledgeItemPicker'
import { KnowledgeAnswerForm } from './KnowledgeAnswerForm'
import { KnowledgeAnswerHistory } from './KnowledgeAnswerHistory'
import { useIntegrationCatalog } from '../integrations/useIntegrationCatalog'
import { StoreKnowledgeCard } from './StoreKnowledgeCard'
import { trackShopifyAction, trackShopifyRefetch } from '../../shopifyUiTracking'
function ItemEditor({
  shopify,
  item,
  questionId,
  origin,
  onSaved,
}: {
  shopify: ShopifyTokenSource
  item: KnowledgeCatalogItem
  questionId?: string
  origin?: 'suggested' | 'manual'
  onSaved: () => void
}) {
  const query = useStoreKnowledge(shopify, { subject: item.subject, questionId })
  const save = useSaveStoreKnowledge(shopify)
  const [failure, setFailure] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const submit = async (input: StoreKnowledgeWrite) => {
    setFailure(null)
    try {
      await save.mutateAsync(input)
      onSaved()
    } catch (error) {
      // Preserve the entered answer and show the failure so the same mutation can be retried.
      console.error('Store knowledge answer could not be saved', error)
      setFailure(resolveMerchantErrorMessage(error, copy.saveFailed))
    }
  }
  let content
  if (query.isPending) content = <s-paragraph>{copy.loading}</s-paragraph>
  else if (query.error)
    content = (
      <s-banner tone="critical">
        {copy.failed}
        <s-button onClick={() => void trackShopifyRefetch(shopify, () => query.refetch())}>{copy.retry}</s-button>
      </s-banner>
    )
  else {
    let entry: KnowledgeEntry | undefined
    entry = query.data.entries[0]
    let history = null
    let reload = null
    if (failure)
      reload = (
        <s-button disabled={query.isFetching} onClick={() => void trackShopifyRefetch(shopify, () => query.refetch())}>
          {copy.reload}
        </s-button>
      )
    let another = null
    let currentAnswer = null
    let withdrawal = null
    if (entry) {
      currentAnswer = (
        <s-text>
          {copy.current}: {answerLabel(entry)}
        </s-text>
      )
      if (showHistory)
        withdrawal = (
          <s-button
            disabled={save.isPending}
            onClick={() =>
              void submit({
                mutationId: crypto.randomUUID(),
                questionId: entry.questionId,
                expectedVersion: entry.version,
                subject: entry.subject,
                scope: entry.relationship.scope,
                response: { kind: 'withdraw', note: '' },
              })
            }
          >
            {copy.withdraw}
          </s-button>
        )
      if (showHistory) history = <KnowledgeAnswerHistory shopify={shopify} entry={entry} />
      another = (
        <s-button
          disabled={save.isPending}
          onClick={() =>
            trackShopifyAction(shopify, showHistory ? 'close-history' : 'open-history', () =>
              setShowHistory(!showHistory),
            )
          }
        >
          {copy.history}
        </s-button>
      )
    }
    content = (
      <s-stack gap="base">
        {currentAnswer}
        <KnowledgeAnswerForm
          key={`${entry?.questionId ?? 'new'}:${entry?.version ?? 0}`}
          shopify={shopify}
          item={item}
          entry={entry}
          origin={origin}
          busy={save.isPending}
          error={failure}
          onSubmit={submit}
        />
        {reload}
        {another}
        {history}
        {withdrawal}
      </s-stack>
    )
  }
  return <s-stack gap="base">{content}</s-stack>
}
function answerLabel(entry: KnowledgeEntry) {
  const answer = entry.answers[0].response
  if (answer.kind === 'answer') return copy.roles[answer.role]
  return copy.responses[answer.kind]
}
function SavedAnswers({
  shopify,
  onSelect,
}: {
  shopify: ShopifyTokenSource
  onSelect: (item: KnowledgeCatalogItem, id: string) => void
}) {
  const [entriesAfter, setEntriesAfter] = useState('')
  const query = useStoreKnowledge(shopify, { entriesAfter })
  let content
  if (query.isPending) content = <s-paragraph>{copy.loading}</s-paragraph>
  else if (query.error)
    content = (
      <s-banner tone="critical">
        {copy.failed}
        <s-button onClick={() => void trackShopifyRefetch(shopify, () => query.refetch())}>{copy.retry}</s-button>
      </s-banner>
    )
  else {
    let empty = null
    if (!query.data.entries.length) empty = <s-paragraph>{copy.noSaved}</s-paragraph>
    let more = null
    if (query.data.entriesNext)
      more = (
        <s-button
          onClick={() => trackShopifyAction(shopify, 'select-page', () => setEntriesAfter(query.data.entriesNext!))}
        >
          {copy.more}
        </s-button>
      )
    content = (
      <s-stack gap="base">
        {empty}
        {query.data.entries.map((entry) => (
          <s-stack key={entry.questionId} gap="small">
            <s-text>{knowledgeItemLabel(entry.answers[0].subjectSnapshot)}</s-text>
            <s-text>{answerLabel(entry)}</s-text>
            <s-button
              onClick={() =>
                trackShopifyAction(shopify, 'select-item', () =>
                  onSelect(entry.answers[0].subjectSnapshot, entry.questionId),
                )
              }
            >
              {copy.open}
            </s-button>
          </s-stack>
        ))}
        {more}
      </s-stack>
    )
  }
  return <s-section heading={copy.savedHeading}>{content}</s-section>
}
function ScopedKnowledgePanel({ shopify }: { shopify: ShopifyTokenSource }) {
  const query = useStoreKnowledge(shopify, {})
  const [selected, setSelected] = useState<{
    item: KnowledgeCatalogItem
    questionId?: string
    origin?: 'suggested' | 'manual'
  } | null>(null)
  const [saved, setSaved] = useState(false)
  const [choosing, setChoosing] = useState(false)
  const [showSaved, setShowSaved] = useState(false)
  let current = selected
  if (!current && query.data?.catalog[0]) current = { item: query.data.catalog[0], origin: 'suggested' }
  let content
  if (saved) content = <s-banner>{copy.quickSaved}</s-banner>
  else if (choosing)
    content = (
      <KnowledgeItemPicker
        shopify={shopify}
        onSelect={(item, origin) => {
          setSelected({ item, origin })
          setChoosing(false)
        }}
      />
    )
  else if (current)
    content = (
      <ItemEditor
        key={JSON.stringify(current)}
        shopify={shopify}
        {...current}
        onSaved={() => {
          setSelected(null)
          setSaved(true)
        }}
      />
    )
  else if (query.isPending) content = <s-paragraph>{copy.loading}</s-paragraph>
  else if (query.error)
    content = (
      <s-banner tone="critical">
        {copy.failed}
        <s-button onClick={() => void trackShopifyRefetch(shopify, () => query.refetch())}>{copy.retry}</s-button>
      </s-banner>
    )
  else content = <s-paragraph>{copy.noQuestions}</s-paragraph>
  let answers = null
  if (showSaved)
    answers = (
      <SavedAnswers
        shopify={shopify}
        onSelect={(item, questionId) => {
          setSelected({ item, questionId })
          setSaved(false)
          setChoosing(false)
        }}
      />
    )
  return (
    <StoreKnowledgeCard>
      {content}
      <s-stack direction="inline" gap="base">
        <s-button
          onClick={() =>
            trackShopifyAction(shopify, 'start-answer', () => {
              setChoosing(true)
              setSaved(false)
            })
          }
        >
          {copy.chooseItem}
        </s-button>
        <s-button
          onClick={() =>
            trackShopifyAction(shopify, showSaved ? 'close-saved' : 'open-saved', () => setShowSaved(!showSaved))
          }
        >
          {copy.savedHeading}
        </s-button>
      </s-stack>
      {answers}
    </StoreKnowledgeCard>
  )
}
export function StoreKnowledgePanel({ shopify }: { shopify: ShopifyTokenSource }) {
  const scope = useShopifyAppContext()
  const catalog = useIntegrationCatalog(shopify, true)
  if (!scope.orgId || !scope.connectionId) return null
  if (catalog.error)
    return (
      <StoreKnowledgeCard>
        <s-banner tone="critical">
          {copy.failed}
          <s-button onClick={() => void trackShopifyRefetch(shopify, () => catalog.refetch())}>{copy.retry}</s-button>
        </s-banner>
      </StoreKnowledgeCard>
    )
  if (!catalog.data?.capabilities?.includes(STORE_KNOWLEDGE_CAPABILITY)) return null
  return <ScopedKnowledgePanel key={buildShopifyAppScopeKey(scope)} shopify={shopify} />
}
